import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import Logo from "../assets/logo.png";
import {
  useGetVisitTypesQuery,
  useGetProviderConfigQuery,
} from "../redux/features/routeQuestionApi";
import StepCategory from "./StepForm/StepCategory";
import StepConfirm from "./StepForm/StepConfirm";
import StepPatientInfo from "./StepForm/StepPatientInfo";
import Stepper from "./StepForm/Stepper";
import StepSchedule from "./StepForm/StepSchedule";
import StepService from "./StepForm/StepService";
import StepQuestionnaire from "./StepForm/StepQuestionnaire";
import StepLocation from "./StepForm/StepLocation";
import StepProvider from "./StepForm/StepProvider";
import AvailabilityDrawer from "./Availability/AvailabilityDrawer";

function StepData() {
  const { data: visitTypesAll } = useGetVisitTypesQuery();
  const { data: providerConfigRes } = useGetProviderConfigQuery();
  const providerConfig = providerConfigRes?.data;

  const savedStep = Number(localStorage.getItem("currentStep")) || 0;
  const savedFormData = JSON.parse(localStorage.getItem("formData") || "{}");

  const [step, setStep] = useState(savedStep);
  const [formData, setFormData] = useState(savedFormData);

  const memberId = formData?.selectedProvider?.member_id;
  const { data: visitTypesForProvider } = useGetVisitTypesQuery(
    memberId ? { member_id: memberId } : skipToken
  );

  const visit_type_id = formData?.service?.visit_type_id;
  const patientid = formData?.patientid;

  useEffect(() => {
    localStorage.setItem("currentStep", step);
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [step, formData]);

  const facilityDataForCategory = visitTypesAll;
  const facilityDataForService = visitTypesForProvider || visitTypesAll;

  const steps = [
    <StepCategory
      key="cat"
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(1)}
      facilityData={facilityDataForCategory}
    />,
    <StepLocation
      key="loc"
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(2)}
      onBack={() => setStep(0)}
      providerConfig={providerConfig}
    />,
    <StepProvider
      key="prov"
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(3)}
      onBack={() => setStep(1)}
      providerConfig={providerConfig}
    />,
    <StepService
      key="svc"
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(4)}
      onBack={() => setStep(2)}
      facilityData={facilityDataForService}
      providerConfig={providerConfig}
    />,
    <StepPatientInfo
      key="pat"
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(5)}
      onBack={() => setStep(3)}
    />,
    <StepQuestionnaire
      key="ques"
      patientid={patientid}
      visit_type_id={visit_type_id}
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(6)}
      onBack={() => setStep(4)}
    />,
    <StepSchedule
      key="sched"
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(7)}
      onBack={() => setStep(5)}
    />,
    <StepConfirm
      key="conf"
      formData={formData}
      setStep={setStep}
    />,
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white flex flex-col items-center justify-center p-8"
      style={{ paddingTop: "70px" }}
    >
      <div className="w-full max-w-5xl flex flex-col items-center text-center mb-8">
        <img
          src={Logo}
          alt="Texas Center Wellness"
          className="object-contain mx-auto mb-5 w-[180px] sm:w-[220px]"
        />
        <p className="text-[#fc8c02] tracking-[0.15em] uppercase font-bold text-sm mb-2">
          Appointments
        </p>
        <h1 className="uppercase font-extrabold leading-tight mb-3 text-[clamp(1.75rem,4vw,2.75rem)]">
          Schedule Your Visit
        </h1>
        <p className="text-gray-300 text-sm sm:text-base max-w-2xl leading-relaxed m-0">
          Take the next step toward personalized, physician-led care. Choose an
          available appointment below to get started with Texas Center
          Wellness.
        </p>
      </div>

      <div className="w-full max-w-5xl bg-gray-900/70 backdrop-blur-md p-6 sm:p-8 rounded-2xl shadow-2xl border border-gray-700">
        <Stepper currentStep={step} onBack={() => setStep(step - 1)} />

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.4 }}
          >
            {steps[step]}
          </motion.div>
        </AnimatePresence>
      </div>
      <AvailabilityDrawer formData={formData} />
    </div>
  );
}

export default StepData;
